// <copyright file="BasicSafetyMessageGeoJson.tsx">
// © 2023 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

import React, { useEffect } from "react";
import { GeoJSON } from "react-leaflet";
import { popupInfoTable } from "src/components/VIPER/utils/MapUtils";
import { v4 as uuidv4 } from "uuid";
import type { FeatureCollection } from "@turf/helpers";
import type { Feature } from "geojson";
import type { default as LEAF } from "leaflet";
import type { FC } from "react";
import type { PopupInfoTableProps } from "src/components/VIPER/utils/MapUtils";

// Properties to initialize this UI component
interface BasicSafetyMessageGeoJsonProps {
    bsmData: FeatureCollection; // bsm data retrieved from back end, state lifted to parent
    colorMap: Map<string, string>; // color per bsm poly
    selectedRSU: string[]; // device filter selections
    outliersIncluded: boolean; // outlier filter selection
}

const BasicSafetyMessageGeoJson: FC<BasicSafetyMessageGeoJsonProps> = (
    props: BasicSafetyMessageGeoJsonProps
) => {
    const geoJsonLayer = React.createRef<LEAF.GeoJSON>();
    let key = uuidv4();

    useEffect(() => {
        // Unique key required to trigger map layer update when data changes
        key = uuidv4();
    }, [props.bsmData, props.selectedRSU]);

    /**
     * Get or set a polygon fill color. Use assigned color if known,
     * else use a random color.
     * @param feature RSU coverage polygon feature
     * @returns Polygon fill color
     */
    function getPolygonColor(feature: Feature | undefined): string {
        const deviceId = feature?.properties?.sourceDeviceId || undefined;
        let color = (deviceId && props.colorMap.get(deviceId)) || undefined;
        if (!color) {
            color = "#" + Math.floor(Math.random() * 16777215).toString(16);
            if (deviceId) {
                props.colorMap.set(deviceId, color);
            }
        }
        return color;
    }

    /**
     * Creates style for an RSU coverage polygon map feature
     * @param feature RSU coverage polygon feature
     * @returns Style (outline color, fill color, opacity, etc) applied to feature
     */
    const style: L.StyleFunction = (
        feature: GeoJSON.Feature | undefined
    ): L.PathOptions => {
        const color = getPolygonColor(feature);
        return {
            fillColor: color,
            fillOpacity: 0.5,
            opacity: 0.3,
            weight: 1,
            color: color,
        };
    };

    /**
     * Callback to add a click event/popup to `layer`
     * @param {GeoJSON.Feature} feature The feature to bind a popup for
     * @param {L.Layer} layer Layer to which to bind the popup/add the click event
     */
    const onEachFeature = (feature: GeoJSON.Feature, layer: L.Layer) => {
        /* eslint-disable @typescript-eslint/ban-ts-comment */
        if (feature.properties) {
            // Set tooltip
            const tableProps: PopupInfoTableProps = {
                title: "RSU Coverage Information:",
                data: Object.entries(feature.properties),
            };
            layer.bindPopup(popupInfoTable(tableProps));

            // Set polygon color
            const color = getPolygonColor(feature);

            // On mouseover, add thick solid white outline and increase fill opacity
            layer.on("mouseover", function () {
                // @ts-ignore use of this on next line
                this.bringToFront(); // NOSONAR
                // @ts-ignore use of this on next line
                this.setStyle({ // NOSONAR
                    color: "#ffffff",
                    weight: 3,
                    opacity: 1.0,
                    fillOpacity: 0.7,
                });
            });

            // On mouseouut, reset outline and fill opacity
            layer.on("mouseout", function () {
                // @ts-ignore use of this on next line
                this.setStyle({ // NOSONAR
                    color: color,
                    weight: 1,
                    opacity: 0.3,
                    fillOpacity: 0.5,
                });
            });
        }
        /* eslint-enable */
    };

    /**
     * Callback to decide whether to filter or not
     * @param {GeoJSON.Feature} feature The feature to decide whether to filter
     * @returns Whether to filter or not
     */
    const filterFunction = (feature: GeoJSON.Feature): boolean => {
        try {
            if (feature.properties) {
                return (
                    props.selectedRSU.includes(
                        feature?.properties?.sourceDeviceId
                    ) &&
                    feature?.properties?.outliersIncluded ==
                        props.outliersIncluded
                );
            } else {
                return false;
            }
        } catch (error) {
            return true;
        }
    };

    return (
        <GeoJSON
            key={key}
            ref={geoJsonLayer}
            data={props.bsmData}
            onEachFeature={onEachFeature}
            style={style}
            filter={filterFunction}
        />
    );
};

export default BasicSafetyMessageGeoJson;
